import { getMeta, setMeta, wipe } from "./db";

/** brandId — бренд, который ведёт сотрудник; от него зависит чек-лист выкладки. */
export type User = { id: string; name: string; role: "rep" | "manager"; brandId?: string | null };

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly wipeLocal = false) {
    super(message);
  }
}

let token: string | null = null;
let user: User | null = null;

export async function restoreSession() {
  token = await getMeta<string | null>("token", null);
  user = await getMeta<User | null>("user", null);
  return { token, user };
}

export const currentUser = () => user;
export const hasSession = () => Boolean(token);

export async function saveSession(t: string, u: User) {
  token = t; user = u;
  await setMeta("token", t);
  await setMeta("user", u);
}

/** Свежие данные о себе пришли с обменом — ключ доступа при этом прежний. */
export async function refreshUser(u: User) {
  user = u;
  await setMeta("user", u);
}

/** Полный выход: стираем и ключ, и локальные данные. */
export async function dropSession() {
  token = null; user = null;
  await wipe();
}

type Options = { method?: string; body?: unknown; raw?: BodyInit; timeoutMs?: number };

export async function api<T = any>(path: string, opts: Options = {}): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 30_000);

  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? "GET",
      headers: {
        ...(opts.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: "Bearer " + token } : {}),
      },
      body: opts.raw ?? (opts.body ? JSON.stringify(opts.body) : undefined),
      signal: ctrl.signal,
    });
  } catch {
    // Нет сети — это норма, а не ошибка: работа продолжается офлайн.
    throw new ApiError("Нет связи", 0);
  } finally {
    clearTimeout(timer);
  }

  const text = await res.text();
  let data: any;
  try { data = text ? JSON.parse(text) : {}; } catch { data = { error: text }; }

  if (!res.ok) {
    // Руководитель отозвал доступ — планшет обязан разлогиниться и стереть данные.
    if (res.status === 401 && data?.wipe) {
      await dropSession();
      throw new ApiError(data.error ?? "Доступ отозван", 401, true);
    }
    throw new ApiError(data?.error ?? `Ошибка ${res.status}`, res.status);
  }
  return data as T;
}

/** Картинка с сервера. Обычный <img src> не подходит: фото отдаются только
 *  вошедшему, а тег сам заголовок с ключом доступа не пошлёт. */
export async function apiBlob(path: string): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(path, {
      headers: token ? { Authorization: "Bearer " + token } : {},
    });
  } catch {
    throw new ApiError("Нет связи", 0);
  }
  if (!res.ok) throw new ApiError(`Ошибка ${res.status}`, res.status);
  return res.blob();
}

export const online = () => navigator.onLine;
